/**
 * MetricNumber — Numero metrica con count-up al mount.
 *
 * Caratteristiche editoriali:
 *  - Tabular nums (allineamento perfetto)
 *  - Font display (Fraunces) per numeri grandi (>=h4): personalita' serif
 *  - Font mono per numeri piccoli/inline (allineamento tecnico)
 *  - Count-up animato con easing ease-out (220ms × 6 frames = morbido)
 *
 * Pattern d'uso:
 *   <MetricNumber value={1240} unit="€" prefix display />
 *   <MetricNumber value={28} delta={+3} deltaTone="positive" />
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { tokens } from '../../theme';

interface MetricNumberProps {
  value: number;
  /** Es. '€' o '%' o 'utenti'. Posizionato secondo `prefix` flag. */
  unit?: string;
  prefix?: boolean;
  /** Decimali da mostrare. */
  decimals?: number;
  /** Usa Fraunces display (grande, editoriale). Default false (Manrope body, mono per integer). */
  display?: boolean;
  /** Override colore numero (e.g. tokens.color.tierBusiness.main). */
  color?: string;
  /** Delta numerico (+3, -1.2). Mostrato a destra con freccia. */
  delta?: number;
  deltaTone?: 'positive' | 'negative' | 'neutral';
  /** Durata count-up in ms (default 700). 0 = no animazione. */
  countUpMs?: number;
  /** Label sopra il numero. */
  label?: string;
  /** Hint piccolo sotto (es. "vs ultimo mese"). */
  caption?: string;
}

function formatNumber(n: number, decimals: number, locale = 'it-IT'): string {
  return n.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export const MetricNumber: React.FC<MetricNumberProps> = ({
  value,
  unit,
  prefix = false,
  decimals = 0,
  display = false,
  color,
  delta,
  deltaTone,
  countUpMs = 700,
  label,
  caption,
}) => {
  const [shown, setShown] = useState(countUpMs > 0 ? 0 : value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (countUpMs <= 0) {
      setShown(value);
      return;
    }
    const start = performance.now();
    const from = 0;
    const to = value;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / countUpMs);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, countUpMs]);

  const formatted = formatNumber(shown, decimals);
  const deltaTonePalette: Record<string, string> = {
    positive: tokens.color.success,
    negative: tokens.color.danger,
    neutral: tokens.color.ash,
  };
  const tone = deltaTone ?? (delta == null ? 'neutral' : delta >= 0 ? 'positive' : 'negative');

  return (
    <Box>
      {label && (
        <Typography
          variant="overline"
          sx={{ display: 'block', mb: 0.5, color: tokens.color.mist }}
        >
          {label}
        </Typography>
      )}
      <Stack direction="row" alignItems="baseline" spacing={1.25}>
        <Box
          sx={{
            fontFamily: display ? tokens.font.display : tokens.font.body,
            fontSize: display ? '2.6rem' : '1.5rem',
            lineHeight: 1,
            fontWeight: display ? 600 : 700,
            letterSpacing: display ? '-0.02em' : '-0.01em',
            color: color ?? tokens.color.graphite,
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: '"tnum" 1, "ss01" 1',
            ...(display && {
              fontVariationSettings: '"WONK" 0, "SOFT" 50, "opsz" 96',
            }),
          }}
        >
          {prefix && unit && (
            <Box
              component="span"
              sx={{
                fontSize: display ? '1.4rem' : '0.9rem',
                opacity: 0.55,
                mr: 0.25,
                fontWeight: 500,
              }}
            >
              {unit}
            </Box>
          )}
          {formatted}
          {!prefix && unit && (
            <Box
              component="span"
              sx={{
                fontSize: display ? '1.1rem' : '0.78rem',
                opacity: 0.55,
                ml: 0.5,
                fontWeight: 500,
              }}
            >
              {unit}
            </Box>
          )}
        </Box>
        {delta != null && (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            {delta >= 0 ? (
              <ArrowDropUp sx={{ fontSize: 18, color: deltaTonePalette[tone], my: '-4px' }} />
            ) : (
              <ArrowDropDown sx={{ fontSize: 18, color: deltaTonePalette[tone], my: '-4px' }} />
            )}
            <Box
              sx={{
                color: deltaTonePalette[tone],
                fontSize: '0.78rem',
                fontWeight: 700,
                fontFamily: tokens.font.body,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {delta > 0 ? '+' : ''}
              {formatNumber(delta, decimals)}
            </Box>
          </Stack>
        )}
      </Stack>
      {caption && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: tokens.color.mist,
            fontSize: '0.72rem',
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};
